import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import { getValidBearerToken } from "../../constants/tokenManager";
import { getDistributorList } from "../../constants/index";
//
// 🔐 API Call (Only Once)
//
export async function fetchProtectedData() {
  try {
    const token = await getValidBearerToken();
    const response = await fetch(getDistributorList, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*", 
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} - ${errorText}`);
    }

    const contentType = response.headers.get("content-type");
    let data;
    if (contentType && contentType.includes("application/json")) {
      console.time("parseJSON");
      data = await response.json();
      console.timeEnd("parseJSON");
    } else {
      const rawText = await response.text();
      try {
        data = JSON.parse(rawText);
      } catch {
        throw new Error("❌ Failed to parse response JSON");
      }
    }

    if (data && typeof data.data === "string") {
      try {
        data.data = JSON.parse(data.data);
      } catch {
        console.warn("Could not parse nested JSON string in data.data");
      }
    }
    console.log("data", data);
    return data;
  } catch (err) {
    console.error("❌ Error fetching protected data:", err);
    throw err;
  }
}

//
// 🚀 Single Thunk: loadDropdownData
//
export const loadDropdownData = createAsyncThunk(
  "dropdown/loadAll",
  async () => {
    const response = await fetchProtectedData();
    const data = response?.data?.value || [];
    if (!response?.data || !Array.isArray(response.data.value)) {
      throw new Error("Expected response.data.value to be an array");
    }

    const rawItems = response.data.value;

    const regions = [
      ...new Set(
        rawItems.map((item) => item.Region?.Title?.trim()).filter(Boolean)
      ),
    ].sort();

    const countries = [
      ...new Set(
        rawItems.map((item) => item.Country?.Title?.trim()).filter(Boolean)
      ),
    ].sort();

    const brands = [
      ...new Set(
        rawItems.flatMap((item) =>
          Array.isArray(item.BrandName)
            ? item.BrandName.map((b) => b?.trim()).filter(Boolean)
            : []
        )
      ),
    ].sort();

    return { regions, countries, brands, allData: data };
  }
);

//
// 🧩 Dropdown Slice
//
export const dropdownSlice = createSlice({
  name: "dropdown",
  initialState: {
    regions: [],
    countries: [],
    brands: [],
    selectedRegion: "",
    selectedCountry: "",
    selectedBrand: "",
    loading: false,
    error: null,
    loaded: false,
    loadingAllItems: "",
  },
  reducers: {
    setSelectedRegion(state, action) {
      state.selectedRegion = action.payload;
      console.log("selectedRegion:", state.selectedRegion);
    },
    setSelectedCountry(state, action) {
      state.selectedCountry = action.payload;
    },
    setSelectedBrand(state, action) {
      state.selectedBrand = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDropdownData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loaded = false;
        state.loadingAllItems = true;
      })
      .addCase(loadDropdownData.fulfilled, (state, action) => {
        const { regions, countries, brands } = action.payload;
        state.regions = action.payload.regions;
        state.countries = action.payload.countries;
        state.brands = action.payload.brands;
        state.allData = action.payload.allData;
        state.loading = false;
        state.loaded = true;
        state.loadingAllItems = false;
      })
      .addCase(loadDropdownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.loaded = false;
      });
  },
});

//
// 📻 Radio Slice
//
export const radioSlice = createSlice({
  name: "radio",
  initialState: {
    selectedOption: "",
  },
  reducers: {
    selectOption(state, action) {
      state.selectedOption = action.payload;
    },
  },
});

//
// 📤 Exports
//
export const { setSelectedRegion, setSelectedCountry, setSelectedBrand } =
  dropdownSlice.actions;

export const { selectOption } = radioSlice.actions;

export const dropdownReducer = dropdownSlice.reducer;
export const radioReducer = radioSlice.reducer;
